import { RowClassParams, RowStyle } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { AgTable } from '../../../../components/AgTable/AgTable';
import { ICaptableDataModel } from '../../../../data-models/captable2.data-model';
import { colors } from '../../../../theme/colors';
import { hasActiveHoldings, hasActiveNoteHolding, isClientInvestment } from '../captable-utils';
import { CaptableGridContainer } from './CaptableGridContainer';
import { CaptableSummaryDefaultColDefs, useCaptableSummaryColDefs } from './CaptableSummaryColDefs';
import { ICaptableSummaryRowData, useCaptableSummaryRowData } from './CaptableSummaryRowData';

export enum StakeholderType {
  client = 'Client',
  external = 'External',
}

const GridHeight = 'calc(100dvh - 25rem)';

export interface ICaptableSummaryGridProps {
  data: ICaptableDataModel;
  quickFilterText?: string;
  investmentFilter?: StakeholderType;
}

export function CaptableSummaryGrid({ data, quickFilterText, investmentFilter }: ICaptableSummaryGridProps) {
  const unfilteredData = useCaptableSummaryRowData(data ?? null);
  const rowData = useMemo(() => {
    if (investmentFilter) {
      return filterOutClientInvestments(unfilteredData, investmentFilter);
    }
    return unfilteredData;
  }, [unfilteredData, investmentFilter]);

  const colDefs = useCaptableSummaryColDefs(data ?? null);
  const { getRowStyle } = useRowStyles();

  const autoHeight = unfilteredData.length <= 10;

  return (
    <CaptableGridContainer style={autoHeight ? undefined : { height: GridHeight, maxHeight: GridHeight }}>
      <AgTable
        quickFilterText={quickFilterText}
        rowData={rowData}
        columnDefs={colDefs}
        grandTotalRow='bottom'
        rowGroupPanelShow='never'
        defaultColDef={CaptableSummaryDefaultColDefs}
        gridOptions={{
          getRowStyle,
        }}
        domLayout={autoHeight ? 'autoHeight' : 'normal'}
      />
    </CaptableGridContainer>
  );
}

export function filterOutClientInvestments(
  data: ICaptableSummaryRowData[],
  investmentFilter: StakeholderType
): ICaptableSummaryRowData[] {
  return data.filter((row) => {
    if (investmentFilter === StakeholderType.client) {
      return (
        isClientInvestment(row) &&
        (hasActiveHoldings(row.totals?.fullyDilutedShares, row.totals?.ownershipPercentage) ||
          hasActiveNoteHolding(row))
      );
    } else if (investmentFilter === StakeholderType.external) {
      return !isClientInvestment(row);
    } else {
      return true;
    }
  });
}

export function useRowStyles() {
  const getRowStyle = useCallback((params: RowClassParams<ICaptableSummaryRowData>): RowStyle | undefined => {
    if (params.data?.fundId) {
      return { background: colors.secondary[5] as string };
    }
  }, []);

  return { getRowStyle };
}
